// Copyright (c) 2026 BlueRock Security, Inc.
// All rights reserved.
//
// BLUEROCK SECURITY, INC. PROPRIETARY AND CONFIDENTIAL INFORMATION

import { Typography, theme } from 'antd';

const { Text } = Typography;

export interface MiniSparklineProps {
  points: number[];
  /** Stroke color. Defaults to AntD `colorPrimary` (brand blue). */
  color?: string;
  height?: number;
  width?: number;
}

/**
 * Mini sparkline from a flat number[] (no timestamps). Renders an
 * inline polyline SVG scaled to the given box; falls back to an
 * em-dash when fewer than two points are provided.
 *
 * Promoted from agate's shared/ui/MiniSparkline — kept deliberately
 * chrome-free (no axes/gridlines/tooltip) so it stays cheap to render
 * many-at-once inside a KpiCard grid.
 */
export function MiniSparkline({ points, color, height = 18, width = 80 }: MiniSparklineProps) {
  const { token } = theme.useToken();
  const strokeColor = color ?? token.colorPrimary;
  if (!points || points.length < 2)
    return (
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        —
      </Text>
    );
  const max = Math.max(...points, 0.001);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const svgPoints = points
    .map((v, i) => `${(i / (points.length - 1)) * width},${height - ((v - min) / range) * height}`)
    .join(' ');
  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      <polyline
        points={svgPoints}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default MiniSparkline;
